import { ADMIN_API_URL } from '../utils/api';
import { doc, setDoc, updateDoc, getDoc, deleteDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';

class AdminApis {
  constructor(storeId, extensionId, email) {
    // Ensure storeId is a number for API calls, but don't hardcode fallback
    this.storeId = parseInt(storeId);
    this.extensionId = extensionId || "4";
    this.email = email;
    
    // Validate that we have required data
    if (!this.storeId || isNaN(this.storeId)) {
      console.error('AdminApis initialized without valid storeId:', storeId);
    }
    
    console.log('AdminApis initialized with:', {
      storeId: this.storeId,
      extensionId: this.extensionId,
      email: this.email
    });
  }

  // ============ DEALER MANAGEMENT (B2B Customers) ============

  // Get all dealers for a store - using Firebase as source of truth with store_id filtering
  async getDealers() {
    try {
      if (!this.storeId || isNaN(this.storeId)) {
        throw new Error('Invalid store ID for fetching dealers');
      }
      
      console.log('Fetching dealers from Firebase for store:', this.storeId);
      
      // 1. Get all dealers from Firebase dealer-portal-users collection for THIS STORE ONLY
      const dealersCollection = collection(db, 'dealer-portal-users');
      const dealersQuery = query(dealersCollection, where('store_id', '==', this.storeId.toString()));
      const dealersSnapshot = await getDocs(dealersQuery);
      
      console.log(`Firebase query for store_id: "${this.storeId.toString()}" returned ${dealersSnapshot.size} dealers`);
      
      if (dealersSnapshot.empty) {
        console.log('No dealers found in Firebase for store:', this.storeId);
        return [];
      }
      
      // 2. Get all customers from Circle API to merge with dealer data
      const customersUrl = `${ADMIN_API_URL}/store/${this.storeId}/customers`;
      const customersResponse = await fetch(customersUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      let allCustomers = [];
      if (customersResponse.ok) {
        const customersData = await customersResponse.json();
        allCustomers = customersData.customers || [];
      } else {
        console.warn('Could not fetch customers from Circle API, using Firebase data only');
      }
      
      // 3. Merge Firebase dealer data with Circle customer data (ONLY for current store)
      const dealers = [];
      dealersSnapshot.forEach(doc => {
        const dealerData = doc.data();
        const dealerEmail = doc.id; // Document ID is the email
        
        // Double-check store_id match for security
        if (dealerData.store_id !== this.storeId.toString()) {
          console.warn(`Dealer ${dealerEmail} has store_id ${dealerData.store_id} but expected ${this.storeId.toString()}`);
          return; // Skip this dealer
        }
        
        // Find corresponding customer data from Circle API
        const customerData = allCustomers.find(customer => 
          customer.email === dealerEmail && !customer.is_deleted
        );
        
        // Create merged dealer object
        const dealer = {
          // Firebase dealer data
          id: dealerData.id || null,
          email: dealerEmail,
          store_id: dealerData.store_id,
          store_name: dealerData.store_name,
          default_profit: dealerData.default_profit,
          hidden_items: dealerData.hidden_items || {
            subcategories: [],
            products: [],
            variationItems: [],
            detailItems: [],
            detailDetailItems: [],
          },
          
          // Circle customer data (if available)
          first_name: customerData?.first_name || dealerData.store_name || 'Unknown Store',
          last_name: customerData?.last_name || 'Dealer',
          phone_number: customerData?.phone_number || '',
          customer_location: customerData?.customer_location || '',
          customer_type: customerData?.customer_type || 'B2B',
          created_at: customerData?.created_at || null,
          
          // Additional flags
          hasCircleAccount: !!customerData,
          isActive: !!customerData && !customerData.is_deleted
        };
        
        dealers.push(dealer);
      });
      
      console.log(`Found ${dealers.length} dealers in Firebase for store ${this.storeId}`);
      return dealers;
      
    } catch (error) {
      console.error('Error fetching dealers:', error);
      return [];
    }
  }

  // Create a new dealer
  async createDealer(dealerData) {
    try {
      if (!this.storeId || isNaN(this.storeId)) {
        return {
          success: false,
          error: 'Invalid store configuration. Please contact administrator.'
        };
      }
      
      // Check if customer is already a dealer in Firebase
      try {
        const dealerDoc = doc(db, 'dealer-portal-users', dealerData.email);
        const dealerDocSnap = await getDoc(dealerDoc);
        
        if (dealerDocSnap.exists()) {
          return {
            success: false,
            error: 'This email is already registered as a dealer.'
          };
        }
      } catch (firebaseError) {
        console.error('Error checking Firebase document:', firebaseError);
        // Continue with creation even if Firebase check fails
      }
      
      console.log('Creating dealer with store_id:', this.storeId, 'extensionId:', this.extensionId);
      
      // Create customer in Circle API with correct fields only
      const response = await fetch(`${ADMIN_API_URL}/customers?extensionId=${this.extensionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          first_name: dealerData.storeName, // Store name goes to first_name
          last_name: dealerData.contactPerson || 'Dealer', // Contact person as last name
          email: dealerData.email,
          phone_number: dealerData.phone,
          customer_location: `${dealerData.address}, ${dealerData.city}, ${dealerData.state} ${dealerData.zipCode}`,
          customer_type: 'B2B',
          store_id: this.storeId // This will be a number from OAuth
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const customer = data.customer;
        
        // Create dealer-portal-users document in Firebase with customer ID as document ID
        try {
          const dealerDoc = doc(db, 'dealer-portal-users', dealerData.email);
          await setDoc(dealerDoc, {
            default_profit: "50", // Always set to 50 as string
            email: dealerData.email,
            id: customer.id.toString(), // Customer ID as string
            store_id: this.storeId.toString(), // Store ID as string from OAuth
            store_name: dealerData.storeName // Store name
          }, { merge: true }); // merge: preserve hidden_items / other fields
          
          console.log('Dealer-portal-users document created successfully with ID:', customer.id);
        } catch (firebaseError) {
          console.error('Error creating Firebase document:', firebaseError);
          // Still return success since Circle API creation succeeded
        }
        
        return {
          success: true,
          customer: customer
        };
      } else {
        const errorData = await response.json();
        console.error('Error creating dealer - Response:', response.status, errorData);
        
        // Provide more specific error messages
        let errorMessage = 'Failed to create dealer';
        if (response.status === 400) {
          errorMessage = errorData.error || 'Invalid data provided';
        } else if (response.status === 404) {
          errorMessage = 'Store or extension not found. Please contact administrator.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error) {
      console.error('Error creating dealer:', error);
      return {
        success: false,
        error: 'Network error while creating dealer'
      };
    }
  }

  // Get dealer details with basic information and order summary
  async getDealerDetails(dealerId) {
    try {
      // Get dealer carts (orders) for basic statistics
      const cartsResponse = await fetch(
        `${ADMIN_API_URL}/customers/${dealerId}/carts?extensionId=${this.extensionId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      let carts = [];
      if (cartsResponse.ok) {
        const cartsData = await cartsResponse.json();
        carts = cartsData.customerCarts || [];
      }

      return {
        carts: carts,
        totalOrders: carts.length,
        totalRevenue: carts.reduce((sum, cart) => sum + (cart.total_amount || 0), 0)
      };
    } catch (error) {
      console.error('Error fetching dealer details:', error);
      return {
        carts: [],
        totalOrders: 0,
        totalRevenue: 0
      };
    }
  }

  // Update dealer information
  async updateDealer(dealerId, dealerData) {
    try {
      if (!this.storeId || isNaN(this.storeId)) {
        return {
          success: false,
          error: 'Invalid store configuration. Please contact administrator.'
        };
      }
      
      console.log('Updating dealer with ID:', dealerId, 'data:', dealerData);
      
      // Update customer in Circle API
      const response = await fetch(`${ADMIN_API_URL}/customers/${dealerId}?extensionId=${this.extensionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          first_name: dealerData.storeName, // Store name goes to first_name
          last_name: dealerData.contactPerson || 'Dealer', // Contact person as last name
          email: dealerData.email,
          phone_number: dealerData.phone,
          customer_location: `${dealerData.address}, ${dealerData.city}, ${dealerData.state} ${dealerData.zipCode}`,
          customer_type: 'B2B',
          store_id: this.storeId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const customer = data.customer;
        
        // Update dealer-portal-users document in Firebase if email changed
        try {
          const dealerDoc = doc(db, 'dealer-portal-users', dealerData.email);
          await setDoc(dealerDoc, {
            default_profit: "50", // Always set to 50 as string
            email: dealerData.email,
            id: customer.id.toString(), // Customer ID as string
            store_id: this.storeId.toString(), // Store ID as string from OAuth
            store_name: dealerData.storeName // Store name
          }, { merge: true }); // merge: preserve hidden_items / other fields
          
          console.log('Dealer-portal-users document updated successfully for ID:', customer.id);
        } catch (firebaseError) {
          console.error('Error updating Firebase document:', firebaseError);
          // Still return success since Circle API update succeeded
        }
        
        return {
          success: true,
          customer: customer
        };
      } else {
        const errorData = await response.json();
        console.error('Error updating dealer - Response:', response.status, errorData);
        
        // Provide more specific error messages
        let errorMessage = 'Failed to update dealer';
        if (response.status === 400) {
          errorMessage = errorData.error || 'Invalid data provided';
        } else if (response.status === 404) {
          errorMessage = 'Dealer not found.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error) {
      console.error('Error updating dealer:', error);
      return {
        success: false,
        error: 'Network error while updating dealer'
      };
    }
  }

  // Get all customers (including non-dealers) for conversion functionality
  async getAllCustomers() {
    try {
      if (!this.storeId || isNaN(this.storeId)) {
        throw new Error('Invalid store ID for fetching customers');
      }
      
      const url = `${ADMIN_API_URL}/store/${this.storeId}/customers`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        // Return all customers (not filtered by type)
        const customers = (data.customers || []).filter(customer => !customer.is_deleted);
        return customers;
      } else {
        const error = await response.json();
        console.error('Error fetching customers:', error);
        return [];
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      return [];
    }
  }

  // Convert existing customer to dealer
  async convertCustomerToDealer(customerId, dealerData) {
    try {
      if (!this.storeId || isNaN(this.storeId)) {
        return {
          success: false,
          error: 'Invalid store configuration. Please contact administrator.'
        };
      }
      
      console.log('Converting customer to dealer with ID:', customerId, 'data:', dealerData);
      
      // Check if customer is already a dealer in Firebase
      try {
        const dealerDoc = doc(db, 'dealer-portal-users', dealerData.email);
        const dealerDocSnap = await getDoc(dealerDoc);
        
        if (dealerDocSnap.exists()) {
          return {
            success: false,
            error: 'This customer is already registered as a dealer.'
          };
        }
      } catch (firebaseError) {
        console.error('Error checking Firebase document:', firebaseError);
        // Continue with conversion even if Firebase check fails
      }
      
      // Update customer type to B2B in Circle API
      const response = await fetch(`${ADMIN_API_URL}/customers/${customerId}?extensionId=${this.extensionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          first_name: dealerData.storeName, // Store name goes to first_name
          last_name: dealerData.contactPerson || 'Dealer', // Contact person as last name
          email: dealerData.email,
          phone_number: dealerData.phone,
          customer_location: `${dealerData.address}, ${dealerData.city}, ${dealerData.state} ${dealerData.zipCode}`,
          customer_type: 'B2B', // Convert to B2B
          store_id: this.storeId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const customer = data.customer;
        
        // Create dealer-portal-users document in Firebase
        try {
          const dealerDoc = doc(db, 'dealer-portal-users', dealerData.email);
          await setDoc(dealerDoc, {
            default_profit: "50", // Always set to 50 as string
            email: dealerData.email,
            id: customer.id.toString(), // Customer ID as string
            store_id: this.storeId.toString(), // Store ID as string from OAuth
            store_name: dealerData.storeName // Store name
          }, { merge: true }); // merge: preserve hidden_items / other fields
          
          console.log('Dealer-portal-users document created successfully for converted customer:', customer.id);
        } catch (firebaseError) {
          console.error('Error creating Firebase document:', firebaseError);
          // Still return success since Circle API conversion succeeded
        }
        
        return {
          success: true,
          customer: customer
        };
      } else {
        const errorData = await response.json();
        console.error('Error converting customer to dealer - Response:', response.status, errorData);
        
        // Provide more specific error messages
        let errorMessage = 'Failed to convert customer to dealer';
        if (response.status === 400) {
          errorMessage = errorData.error || 'Invalid data provided';
        } else if (response.status === 404) {
          errorMessage = 'Customer not found.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error) {
      console.error('Error converting customer to dealer:', error);
      return {
        success: false,
        error: 'Network error while converting customer to dealer'
      };
    }
  }

  // Delete dealer (remove from Firebase and optionally from Circle API)
  async deleteDealer(dealerId, dealerEmail) {
    try {
      if (!this.storeId || isNaN(this.storeId)) {
        return {
          success: false,
          error: 'Invalid store configuration. Please contact administrator.'
        };
      }
      
      console.log('Deleting dealer with ID:', dealerId, 'email:', dealerEmail);
      
      // First, remove from Firebase dealer-portal-users collection
      try {
        const dealerDoc = doc(db, 'dealer-portal-users', dealerEmail);
        await deleteDoc(dealerDoc);
        console.log('Dealer removed from Firebase successfully');
      } catch (firebaseError) {
        console.error('Error removing dealer from Firebase:', firebaseError);
        return {
          success: false,
          error: 'Failed to remove dealer from authentication system.'
        };
      }
      
      // Optionally, you can also delete the customer from Circle API
      // or convert them back to B2C customer type
      // For now, we'll convert them back to B2C to preserve order history
      try {
        const response = await fetch(`${ADMIN_API_URL}/customers/${dealerId}?extensionId=${this.extensionId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            customer_type: 'B2C' // Convert back to regular customer
          }),
        });

        if (!response.ok) {
          console.error('Warning: Failed to convert dealer back to regular customer in Circle API');
          // Don't fail the operation since Firebase deletion succeeded
        } else {
          console.log('Dealer converted back to regular customer in Circle API');
        }
      } catch (apiError) {
        console.error('Warning: Error updating customer type in Circle API:', apiError);
        // Don't fail the operation since Firebase deletion succeeded
      }
      
      return {
        success: true,
        message: 'Dealer removed successfully. They will no longer have access to the dealer portal.'
      };
    } catch (error) {
      console.error('Error deleting dealer:', error);
      return {
        success: false,
        error: 'Network error while deleting dealer'
      };
    }
  }

  // Save a dealer's per-product visibility settings (hide-list).
  // hiddenItems: { products, variationItems, detailItems, detailDetailItems } arrays of IDs.
  // Uses updateDoc so other dealer fields (default_profit, store_name, etc.) are preserved.
  async updateDealerHiddenItems(dealerEmail, hiddenItems) {
    try {
      if (!dealerEmail) {
        return { success: false, error: 'Missing dealer email' };
      }

      const safe = {
        subcategories: (hiddenItems?.subcategories || []).map(Number),
        products: (hiddenItems?.products || []).map(Number),
        variationItems: (hiddenItems?.variationItems || []).map(Number),
        detailItems: (hiddenItems?.detailItems || []).map(Number),
        detailDetailItems: (hiddenItems?.detailDetailItems || []).map(Number),
      };

      const dealerDoc = doc(db, 'dealer-portal-users', dealerEmail);
      await updateDoc(dealerDoc, { hidden_items: safe });

      console.log('Updated hidden_items for dealer', dealerEmail, safe);
      return { success: true, hidden_items: safe };
    } catch (error) {
      console.error('Error updating dealer hidden_items:', error);
      return { success: false, error: 'Failed to save visibility settings' };
    }
  }

  // ============ REFERENCE DATA ============

  // Get categories
  async getCategories() {
    try {
      const response = await fetch(`${ADMIN_API_URL}/categories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.categories || [];
      } else {
        console.error('Error fetching categories');
        return [];
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }

  // Get brands
  async getBrands() {
    try {
      const response = await fetch(`${ADMIN_API_URL}/brands`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.brands || [];
      } else {
        console.error('Error fetching brands');
        return [];
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      return [];
    }
  }

  // Get suppliers
  async getSuppliers() {
    try {
      const response = await fetch(`${ADMIN_API_URL}/suppliers`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.suppliers || [];
      } else {
        console.error('Error fetching suppliers');
        return [];
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      return [];
    }
  }
}

export default AdminApis; 